/**
 * 后台管理 · 场景类型 Tab (scene_category)
 *
 * 与 TaskTypeTab 同风格:表格 + 搜索栏 + 新建/编辑弹窗。
 * 详见 docs/scene-category-design.md v2 §5。
 *
 * 关键交互:
 * - "类型标识" (function_tag) 用 AutoComplete 预填 PL/PLC/PLO/PR/PE/MP,允许自定义
 * - "支持的任务类型" 多选,options 从 /task-type/list 拉;
 *   options 按当前 form.simType 过滤,让运营视线不跳(可以在弹窗内切换后重新过滤)
 * - 删除走 Popconfirm,后端软删;不做级联守护
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  App, AutoComplete, Button, Form, Input,
  Modal, Popconfirm, Radio, Select, Space, Table, Tag, Tooltip,
} from 'antd';
import {
  DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  createSceneCategory, deleteSceneCategory, listSceneCategoriesForAdmin,
  listTaskTypesForAdmin, updateSceneCategory,
  SUGGESTED_FUNCTION_TAGS,
  type SceneCategory,
  type SimType,
  type TaskTypeDetail,
} from '@apis/simulation';

type EditorMode = 'create' | 'edit';

interface EditorState {
  open: boolean;
  mode: EditorMode;
  record?: SceneCategory;
}

interface FormValues {
  name: string;
  functionTag: string;
  simType: SimType;
  taskTypeIds: number[];
  description?: string;
}

// UI 展示配置:sim_type 用与全站一致的色标,Tag 颜色映射一处收敛
const SIM_TYPE_OPTIONS: {
  value: SimType;
  label: string;
  color: string;
}[] = [
  { value: 'planning',   label: 'PNC 仿真', color: 'geekblue' },
  { value: 'perception', label: '感知仿真', color: 'green' },
  { value: 'map',        label: '地图仿真', color: 'orange' },
  { value: 'other',      label: '其他',     color: 'default' },
];

const simTypeLabel = (v?: SimType | null): string => {
  const hit = SIM_TYPE_OPTIONS.find(o => o.value === v);
  return hit ? hit.label : (v ?? '-');
};
const simTypeColor = (v?: SimType | null): string => {
  const hit = SIM_TYPE_OPTIONS.find(o => o.value === v);
  return hit ? hit.color : 'default';
};

const formatTime = (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-');

const EMPTY_EDITOR: EditorState = { open: false, mode: 'create' };

export default function SceneCategoryTab() {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  // 列表 state
  const [data, setData] = useState<SceneCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [simTypeFilter, setSimTypeFilter] = useState<SimType | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR);

  // 任务类型 options(编辑弹窗多选用)
  const [taskTypes, setTaskTypes] = useState<TaskTypeDetail[]>([]);
  const [taskTypesLoaded, setTaskTypesLoaded] = useState(false);

  const loadList = useCallback(async (
    name?: string,
    simType?: SimType,
  ) => {
    setLoading(true);
    try {
      const rows = await listSceneCategoriesForAdmin({
        ...(name ? { name } : {}),
        ...(simType ? { simType } : {}),
      });
      setData(rows);
    } catch (e: any) {
      message.error(e?.message || '加载场景类型失败');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTaskTypesOnce = useCallback(async () => {
    if (taskTypesLoaded) return;
    try {
      const rows = await listTaskTypesForAdmin({});
      setTaskTypes(rows);
      setTaskTypesLoaded(true);
    } catch {
      // 静默:任务类型 options 拉失败不阻塞主列表;弹窗打开时用户会看到空 options
    }
  }, [taskTypesLoaded]);

  useEffect(() => {
    loadList(searchName || undefined, simTypeFilter);
  // 仅 simTypeFilter / name 手动搜索触发变化,不追 searchName 打字每帧刷
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simTypeFilter]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ── 弹窗 ──────────────────────────────────────────────

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      simType: simTypeFilter ?? 'planning',
      taskTypeIds: [],
    });
    setEditor({ open: true, mode: 'create' });
    loadTaskTypesOnce();
  };

  const openEdit = (record: SceneCategory) => {
    form.resetFields();
    form.setFieldsValue({
      name: record.name,
      functionTag: record.functionTag,
      simType: (record.simType ?? 'planning') as SimType,
      taskTypeIds: record.taskTypeIds ?? [],
      description: record.description ?? undefined,
    });
    setEditor({ open: true, mode: 'edit', record });
    loadTaskTypesOnce();
  };

  const closeEditor = () => setEditor(EMPTY_EDITOR);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        functionTag: values.functionTag.trim(),
        simType: values.simType,
        taskTypeIds: values.taskTypeIds ?? [],
        description: values.description || undefined,
      };
      setSubmitting(true);
      if (editor.mode === 'create') {
        await createSceneCategory(payload);
        message.success('新建成功');
      } else if (editor.record) {
        await updateSceneCategory({ id: editor.record.id, ...payload });
        message.success('更新成功');
      }
      closeEditor();
      await loadList(searchName || undefined, simTypeFilter);
    } catch (e: any) {
      if (e?.errorFields) return; // 表单校验失败
      message.error(e?.message || '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (record: SceneCategory) => {
    try {
      await deleteSceneCategory(record.id);
      message.success('删除成功');
      await loadList(searchName || undefined, simTypeFilter);
    } catch (e: any) {
      message.error(e?.message || '删除失败');
    }
  };

  // ── 表格 ──────────────────────────────────────────────

  // task_type_id -> task_type name 映射,用于表格展示 taskTypeIds
  const taskTypeNameById = useMemo(() => {
    const m = new Map<number, string>();
    taskTypes.forEach(t => m.set(t.id, t.name));
    return m;
  }, [taskTypes]);

  const columns: ColumnsType<SceneCategory> = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '场景类型', dataIndex: 'name', width: 140, ellipsis: true },
    {
      title: '类型标识',
      dataIndex: 'functionTag',
      width: 110,
      render: (v: string) => <Tag style={{ fontFamily: 'ui-monospace, monospace' }}>{v}</Tag>,
    },
    {
      title: '归属仿真',
      dataIndex: 'simType',
      width: 110,
      render: (v: SimType) => <Tag color={simTypeColor(v)}>{simTypeLabel(v)}</Tag>,
    },
    {
      title: '支持的任务类型',
      dataIndex: 'taskTypeIds',
      width: 280,
      render: (ids: number[]) => {
        if (!ids?.length) {
          return <span style={{ color: '#8c8c8c', fontSize: 12 }}>未配置(任何任务类型都能支持)</span>;
        }
        return (
          <Space size={4} wrap>
            {ids.map(id => (
              <Tag key={id} color="geekblue">
                {taskTypeNameById.get(id) || `#${id}`}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
      render: (v: string | null | undefined) => v || '-',
    },
    { title: '创建人', dataIndex: 'creator', width: 100, render: v => v || '-' },
    { title: '创建时间', dataIndex: 'createTime', width: 170, render: formatTime },
    {
      title: '操作', key: 'actions', width: 140, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="删除场景类型"
            description="删除后该 function_tag 场景失去元数据(展示名/归属仿真等)。历史场景数据不受影响。"
            okText="删除" cancelText="取消" okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 编辑弹窗中,"支持的任务类型" options 按当前 form 里的 simType 过滤
  // (跟场景类型归属对齐,减少运营勾错跨仿真的可能;留空 simType 时显示全部)
  const currentSimType = Form.useWatch('simType', form);
  const taskTypeOptions = useMemo(() => {
    const filtered = currentSimType
      ? taskTypes.filter(t => (t as any).simType === currentSimType || t.simType === currentSimType)
      : taskTypes;
    return filtered.map(t => ({
      value: t.id,
      label: t.name,
    }));
  }, [taskTypes, currentSimType]);

  return (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          placeholder="按名称搜索"
          prefix={<SearchOutlined />}
          style={{ width: 240 }}
          value={searchName}
          onChange={e => setSearchName(e.target.value)}
          onPressEnter={() => loadList(searchName || undefined, simTypeFilter)}
        />
        <Select
          allowClear
          placeholder="归属仿真:全部"
          style={{ width: 160 }}
          value={simTypeFilter}
          onChange={(v: SimType | undefined) => setSimTypeFilter(v)}
          options={SIM_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
        />
        <Button icon={<SearchOutlined />} onClick={() => loadList(searchName || undefined, simTypeFilter)}>搜索</Button>
        <Button icon={<ReloadOutlined />} onClick={() => { setSearchName(''); setSimTypeFilter(undefined); }}>
          重置
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建场景类型
        </Button>
      </Space>

      <Table<SceneCategory>
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        scroll={{ x: 1400 }}
        size="middle"
      />

      <Modal
        open={editor.open}
        title={editor.mode === 'create' ? '新建场景类型' : `编辑场景类型 #${editor.record?.id}`}
        onCancel={closeEditor}
        onOk={submit}
        confirmLoading={submitting}
        destroyOnHidden
        okText="保存"
        cancelText="取消"
        width={640}
      >
        <Form form={form} layout="vertical" preserve={false} autoComplete="off">
          <Form.Item
            name="name"
            label="场景类型"
            rules={[{ required: true, message: '请输入场景类型名称', whitespace: true }, { max: 64 }]}
          >
            <Input placeholder="例如:PNC 开环 / 感知 / 地图" />
          </Form.Item>

          <Form.Item
            name="functionTag"
            label={(
              <Space size={4}>
                <span>类型标识</span>
                <Tooltip title="与 scene_inspection.function_tag 值等同;一场景一 tag,一 tag 一 category">
                  <span style={{ color: '#bfbfbf', cursor: 'help' }}>ⓘ</span>
                </Tooltip>
              </Space>
            )}
            rules={[{ required: true, message: '请输入类型标识', whitespace: true }, { max: 16 }]}
          >
            <AutoComplete
              placeholder="PL / PLC / PLO / PR / PE / MP;可自定义扩展"
              options={SUGGESTED_FUNCTION_TAGS.map(t => ({ value: t }))}
              filterOption={(input, option) =>
                (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="simType"
            label="归属仿真"
            rules={[{ required: true, message: '请选择归属仿真' }]}
          >
            <Radio.Group>
              {SIM_TYPE_OPTIONS.map(o => (
                <Radio.Button key={o.value} value={o.value}>{o.label}</Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="taskTypeIds"
            label={(
              <Space size={4}>
                <span>支持的任务类型</span>
                <span style={{ color: '#8c8c8c', fontSize: 12 }}>(可空;为空 = 任何任务类型都能支持)</span>
              </Space>
            )}
          >
            <Select
              mode="multiple"
              placeholder="选择该场景类型支持的任务类型(下拉按归属仿真过滤)"
              options={taskTypeOptions}
              optionFilterProp="label"
              allowClear
            />
          </Form.Item>

          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} placeholder="可选" maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
